import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type orientationType,
  useResizeObserver,
  WidthOnly,
} from "../responsive";

type activationIndicatorStyles = {
  height?: number;
  left?: number;
  top?: number;
  width?: number;
  hasChanged?: boolean;
};

const MEASUREMENTS: Record<
  orientationType,
  { pos: "left" | "top"; size: "width" | "height" }
> = {
  horizontal: {
    pos: "left",
    size: "width",
  },
  vertical: {
    pos: "top",
    size: "height",
  },
};

// Overflow can affect tab positions, so we recalculate when it changes
export function useActivationIndicator({
  rootRef,
  tabId,
  orientation,
}: {
  rootRef: RefObject<HTMLDivElement | null>;
  tabId?: string | null;
  orientation: orientationType;
}): activationIndicatorStyles {
  const [style, setStyle] = useState<activationIndicatorStyles>({
    left: 0,
    width: 0,
  });
  // Keep style in a ref, so style is not a dependency for createIndicatorStyle, which in turn
  // means our useEffect below will re-run only when the tab changes, not after every style change
  // as well.

  const styleRef = useRef(style);

  const getTabPos = useCallback(() => {
    const { pos, size } = MEASUREMENTS[orientation];
    return [pos, size];
  }, [orientation]);

  const createIndicatorStyle = useCallback(
    (tabElement: HTMLElement | null): activationIndicatorStyles => {
      if (tabElement) {
        const tabRect = tabElement.getBoundingClientRect();
        if (rootRef.current && tabRect) {
          const rootRect = rootRef.current.getBoundingClientRect();
          const [pos, size] = getTabPos();
          const { [pos]: existingPos, [size]: existingSize } = styleRef.current;
          const newPos = tabRect[pos] - rootRect[pos];
          const newSize = tabRect[size];

          return {
            [pos]: newPos,
            [size]: newSize,
            hasChanged: newPos !== existingPos || newSize !== existingSize,
          };
        }
      }
      return {};
    },
    [rootRef, getTabPos],
  );

  const onResize = useCallback(() => {
    requestAnimationFrame(() => {
      if (tabId) {
        const tabEl = document.getElementById(tabId);
        const { hasChanged, ...newStyle } = createIndicatorStyle(tabEl);
        if (hasChanged) {
          setStyle(newStyle);
        }
      }
    });
  }, [createIndicatorStyle, tabId]);

  useEffect(() => {
    if (tabId) {
      // The timeout is employed in case selectedTab has been selected from
      // overflow menu. In this case, the tab is only restored to visibility
      // in the render cycle after selection.
      setTimeout(() => {
        const tabEl = document.getElementById(tabId);
        const { hasChanged, ...newStyle } = createIndicatorStyle(tabEl);
        if (hasChanged) {
          styleRef.current = newStyle;
          setStyle(newStyle);
        }
      }, 50);
    }
  }, [createIndicatorStyle, tabId]);

  useResizeObserver(rootRef, WidthOnly, onResize);

  return style;
}
